'use client';

import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../redux/store';
import { setCurrentTeam, getScrollInfoV2, selectCurrentTeam } from '../redux/slices/dataSlice';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface Team {
  id: string;
  name: string;
  displayName: string;
  company: string;
  type: 'master' | 'sub';
  isActive: boolean;
}

export default function TeamSwitcher() {
  const { t } = useTranslation('nav');
  const dispatch = useDispatch<AppDispatch>();
  const { isMasterAccount, allTeamsData } = useSelector(
    (state: RootState) => state.masterSubAccount
  );
  const currTeam = useSelector(selectCurrentTeam);
  
  const [isOpen, setIsOpen] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  // Build teams list
  useEffect(() => {
    if (isMasterAccount && allTeamsData) {
      setTeams(allTeamsData);
      
      // Set initial selected team
      if (currTeam && allTeamsData.length > 0) {
        const current = allTeamsData.find(t => t.name === currTeam.name);
        if (current) {
          setSelectedTeam(current);
        } else {
          // Default to master account team
          const masterTeam = allTeamsData.find(t => t.type === 'master');
          if (masterTeam) {
            setSelectedTeam(masterTeam);
            dispatch(setCurrentTeam({ id: masterTeam.id, name: masterTeam.name }));
          }
        }
      } else if (allTeamsData.length > 0) {
        // No current team set, default to master
        const masterTeam = allTeamsData.find(t => t.type === 'master');
        if (masterTeam) {
          setSelectedTeam(masterTeam);
          dispatch(setCurrentTeam({ id: masterTeam.id, name: masterTeam.name }));
        }
      }
    }
  }, [isMasterAccount, allTeamsData, currTeam, dispatch]);

  // Handle team selection
  const handleTeamSelect = async (team: Team) => {
    logger.log('🔄 Switching to team:', team);
    logger.log('📝 Team name for API:', team.name);
    logger.log('📝 Team display:', team.company);
    
    setSelectedTeam(team);
    setIsOpen(false);
    
    // Update current team in Redux (must be object with id and name)
    dispatch(setCurrentTeam({ id: team.id, name: team.name }));
    
    try {
      // Fetch SmartProbes data for the new team (similar to mobile app)
      logger.log('📊 Fetching SmartProbes data for team:', team.name);
      await dispatch(getScrollInfoV2({
        teamName: team.name,  // Pass the team name explicitly
        page: 1,
        pageSize: 20,
        filter: "id",
        ascend: true
      })).unwrap();
      
      logger.log('✅ Team switched and data fetched successfully');
    } catch (error) {
      logger.error('❌ Error fetching SmartProbes for new team:', error);
      logger.log('⚠️ Team switched but data fetch failed');
    }
  };

  // Don't show for non-master accounts
  if (!isMasterAccount || !teams || teams.length === 0) {
    return null;
  }

  const currentDisplayName = selectedTeam 
    ? selectedTeam.company 
    : t('selectTeam');

  return (
    <div className="relative">
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-all duration-200 shadow-sm cursor-pointer"
      >
        <span className="text-sm text-gray-900">{t('currentTeam')}</span>
        <span className="text-sm font-medium text-gray-900">
          {currentDisplayName}
        </span>
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute top-full left-0 mt-2 w-full min-w-[300px] bg-white rounded-lg shadow-xl border border-gray-200 z-20 max-h-96 overflow-y-auto">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-800 uppercase tracking-wider">
                {t('selectTeam')}
              </div>
              
              {teams.map((team) => (
                <button
                  key={team.id}
                  onClick={() => handleTeamSelect(team)}
                  className={`w-full text-left px-3 py-3 rounded-lg transition-colors cursor-pointer ${
                    selectedTeam?.id === team.id
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-blue-50 hover:border-blue-200 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    {/* Team Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {team.company}
                        </p>
                        {!team.isActive && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-semibold rounded">
                            {t('inactive')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-800 truncate">
                        {team.displayName === 'Master Account' ? t('masterAccount') : team.displayName}
                      </p>
                    </div>
                    
                    {/* Checkmark */}
                    {selectedTeam?.id === team.id && (
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

